<template>
    <page
        id="project-request-editor"
        :title="page_title"
        :status="page_status"
        :waiting="waiting"
        @statused="statused"
    >
        <template v-slot:title>
            <div class="uk-margin-bottom lam-page-title">
                <div class="uk-container uk-container-center lam-page-main-title">
                    <h1 class="uk-grid uk-grid-small">
                        <div class="uk-width-3-5">
                            <div class="lam-nowrap-ellipsis">{{ page_title }}</div>
                        </div>
                        <div class="uk-width-2-5 uk-text-right lam-page-title-view">
                            <a href="#main-nav" class="uk-icon-hover uk-icon-arrow-circle-up lam-page-scroll-up" data-uk-smooth-scroll></a>
                            <div>
                                <span v-if="is_draft" class="uk-text-italic">Draft</span>
                                <div v-else class="uk-grid uk-grid-small uk-flex-right" data-uk-grid-margin>
                                    <div>
                                        <base-button
                                            size="large"
                                            color="danger"
                                            @click.native.prevent="confirm_verdict(false)"
                                        >
                                            Deny
                                        </base-button>
                                    </div>
                                    <div>
                                        <base-button
                                            size="large"
                                            color="success"
                                            @click.native.prevent="confirm_verdict(true)"
                                        >
                                            Accept
                                        </base-button>
                                    </div>
                                    <div>
                                        <base-button
                                            size="large"
                                            color="secondary"
                                            class="uk-margin-large-left"
                                            @click.native.prevent="copy"
                                        >
                                            Copy Text Summary
                                        </base-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </h1>
                </div>
            </div>
        </template>
        <div class="uk-container uk-container-center">
            <form class="uk-form lam-edit-screen lam-bordered-columns uk-grid uk-margin-bottom" v-if="edited_record">
                <div class="uk-width-large-4-10 uk-width-small-1-1 uk-margin-bottom">
                    <div v-if="!can_edit_record_data">
                        <h1>{{ edited_record.name }}</h1>
                    </div>
                    <div class="uk-grid uk-grid-small uk-flex-top" data-uk-grid-margin>
                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-small-top">
                            Champion
                        </div>
                        <form-select-user
                            id="champion_id"
                            name="champion_id"
                            v-model="edited_record.champion_id"
                            empty-label="(Pick one)"
                            :required="false"
                            :as-row="false"
                            class="uk-width-3-5"
                            @input="save_field"
                        />
                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            Who else got the email?
                        </div>
                        <div class="uk-width-3-5">
                            <div class="lam-designer-pickers horizontal">
                                <div
                                    v-for="other_recipient_id in sorted_other_recipient_ids"
                                    :key="other_recipient_id"
                                    class="uk-button-group lam-designer-picker"
                                >
                                    <base-button
                                        class="lam-designer-picker-remove"
                                        @click.prevent="remove_other_recipient(other_recipient_id)"
                                    >
                                        <open-icon glyph="close" />
                                    </base-button>
                                    <base-button
                                        :disabled="true"
                                        class="lam-designer-picker-initials"
                                    >
                                        {{ user_by_id(other_recipient_id).initials }}
                                    </base-button>
                                    <dropdown-button
                                        data-uk-dropdown="{pos:'bottom-right'}"
                                        class="lam-designer-picker-dropdown"
                                    >
                                        <template v-slot:dropdown>
                                            <ul class="uk-nav uk-nav-dropdown">
                                                <li
                                                    v-for="other_recipient in available_other_recipients(other_recipient_id)"
                                                    :key="other_recipient.id"
                                                >
                                                    <a href @click.prevent="change_other_recipients(other_recipient.id, other_recipient_id)">
                                                        {{ other_recipient.initials }}
                                                    </a>
                                                </li>
                                            </ul>
                                        </template>
                                        <open-icon glyph="chevron-down" />
                                    </dropdown-button>
                                </div>
                                <div
                                    v-if="available_other_recipients(0).length"
                                    class="uk-button-group lam-designer-picker add-new"
                                >
                                    <dropdown-button
                                        data-uk-dropdown="{pos:'bottom-right'}"
                                        class="lam-designer-picker-dropdown"
                                    >
                                        <template v-slot:dropdown>
                                            <ul class="uk-nav uk-nav-dropdown">
                                                <li
                                                    v-for="other_recipient in available_other_recipients(0)"
                                                    :key="other_recipient.id"
                                                >
                                                    <a href @click.prevent="change_other_recipients(other_recipient.id)">
                                                        {{ other_recipient.initials }}
                                                    </a>
                                                </li>
                                            </ul>
                                        </template>
                                        <open-icon glyph="plus" />
                                    </dropdown-button>
                                </div>
                            </div>
                        </div>

                        <div
                            :class="{ 'uk-text-danger': !edited_record.name }"
                            class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top"
                        >
                            Project Name<sup>*</sup>
                        </div>
                        <div class="uk-width-3-5">
                            <form-input
                                id="name"
                                v-model="edited_record.name"
                                name="name"
                                :error="has_error('name')"
                                :required="true"
                                placeholder="Project Name"
                                width="1-1"
                                @input="save_field"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            &nbsp;
                        </div>
                        <div class="uk-width-1-2">
                            <form-radio
                                id="create_as_additional_scope-false"
                                name="create_as_additional_scope"
                                v-model="edited_record.additional_info.create_as_additional_scope"
                                :true-value="false"
                                :false-value="false"
                                @input="save_additional_info"
                            >
                                New Project
                            </form-radio>
                            <form-radio
                                id="create_as_additional_scope-true"
                                name="create_as_additional_scope"
                                v-model="edited_record.additional_info.create_as_additional_scope"
                                @input="save_additional_info"
                            >
                                <span v-if="edited_record.create_as_additional_scope">Additional Scope of</span>
                                <span v-else>Additional Scope</span>
                            </form-radio>
                            <project-picker
                                v-if="edited_record.additional_info.create_as_additional_scope"
                                id="additional_scope_of_id"
                                name="additional_scope_of_id"
                                :value="edited_record.additional_info.additional_scope_of_id"
                                :single-choice="true"
                                instructions="(Type-in a project name, nickname or number)"
                                :horizontal="false"
                                class="uk-margin-small-top"
                                @pick="save_additional_info"
                            />
                        </div>

                        <div
                            :class="{ 'uk-text-danger': !current_physical_address || (!current_physical_address.state && !current_physical_address.city) }"
                            class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top"
                        >
                            Location<sup>*</sup>
                        </div>
                        <div class="uk-width-3-5">
                            <auto-save-address-form
                                v-model="current_physical_address"
                                entity="address"
                                :highlighted="false"
                                :required="true"
                                class="uk-width-1-1"
                            />
                        </div>

                        <div
                            :class="{ 'uk-text-danger': !edited_record.firm_id }"
                            class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top"
                        >
                            Client<sup>*</sup>
                        </div>
                        <div class="uk-width-3-5">
                            <firm-picker
                                id="firm_id"
                                :value="edited_record.firm_id"
                                @pick="save_field($event.id, 'firm_id')"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            Owner
                        </div>
                        <div class="uk-width-3-5">
                            <firm-picker
                                id="owner_id"
                                :value="edited_record.additional_info.owner_id"
                                @pick="save_additional_info($event.id, 'owner_id')"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            Project Size
                        </div>
                        <div class="uk-width-3-5">
                            <form-size-control
                                id="size"
                                name="size"
                                unit-name="size_units"
                                :value="edited_record"
                                :required="false"
                                @input="save_field"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            Construction Cost
                        </div>
                        <div class="uk-width-3-5">
                            <form-input
                                id="estimated_construction_cost"
                                type="float"
                                v-model="edited_record.additional_info.estimated_construction_cost"
                                glyph="dollar"
                                name="estimated_construction_cost"
                                :required="false"
                                width="1-1"
                                @input="save_additional_info"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            Project Type
                        </div>
                        <div class="uk-width-3-5">
                            <category-picker
                                id="project_type"
                                v-model="edited_record.category_ids"
                                title="Project Type(s)"
                                @change="toggle_category"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            Confidential
                        </div>
                        <div class="uk-width-3-5">
                            <form-checkbox
                                id="confidential"
                                v-model="edited_record.confidential"
                                name="confidential"
                                class="uk-margin-small-top"
                                @input="save_field"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-2-5 uk-margin-top">
                            Estimated Fee
                        </div>
                        <div class="uk-width-3-5">
                            <form-input
                                id="estimated_fee"
                                v-model="edited_record.estimated_fee"
                                name="estimated_fee"
                                :required="false"
                                width="1-1"
                                @input="save_field"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-width-1-1 uk-margin-top">
                            Internal Notes (does not copy)
                        </div>
                        <div class="uk-width-1-1">
                            <form-text-area
                                id="notes"
                                name="notes"
                                v-model="edited_record.additional_info.notes"
                                width="1-1"
                                :required="false"
                                @input="save_additional_info"
                            />
                        </div>
                    </div>
                </div>
                <div class="uk-width-large-3-10 uk-width-small-1-1 uk-margin-bottom">
                    <h3>Schedule</h3>
                    <div class="uk-grid uk-grid-small uk-flex-top" data-uk-grid-margin>
                        <div
                            :class="{ 'uk-text-danger': !edited_record.starts_at }"
                            class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-small-top"
                        >
                            Approx. Lam Start Date<sup>*</sup>
                        </div>
                        <div class="uk-width-2-5">
                            <form-date-input
                                id="starts_at"
                                v-model="edited_record.starts_at"
                                name="starts_at"
                                :min-date="0"
                                format="MM-YYYY"
                                :required="true"
                                @input="save_field"
                            />
                        </div>

                        <div
                            v-for="(value, phase) in edited_record.additional_info.schedule"
                            :key="phase"
                            class="uk-width-1-1"
                        >
                            <div
                                class="uk-grid uk-grid-small"
                                data-uk-grid-margin
                            >
                                <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-small-top">
                                    {{ phase.toUpperCase() }}
                                </div>
                                <div class="uk-width-2-5">
                                    <form-date-input
                                        :id="phase"
                                        v-model="edited_record.additional_info.schedule[phase]"
                                        :name="phase"
                                        :min-date="0"
                                        format="MM-YYYY"
                                        :required="false"
                                        placeholder="End Date"
                                        @input="save_schedule"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <h3>Requests for Material</h3>
                    <div class="uk-grid uk-grid-small uk-flex-top">
                        <div
                            :class="{ 'uk-text-danger': !edited_record.due_at }"
                            class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-small-top"
                        >
                            Content Due by<sup>*</sup>
                        </div>
                        <div class="uk-width-2-5">
                            <form-date-input
                                id="due_at"
                                v-model="edited_record.due_at"
                                name="due_at"
                                :min-date="0"
                                :required="true"
                                @input="save_field"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-top">
                            On the Team only
                        </div>
                        <div class="uk-width-2-5 uk-margin-top">
                            <form-checkbox
                                id="requests_for_materials"
                                :value="!!edited_record.additional_info.requests_for_materials && !edited_record.additional_info.requests_for_materials.length"
                                name="team"
                                :shortkey="['ctrl', 'shift', 't']"
                                @input="toggle_requests_for_material('*')"
                            >
                                <!-- <span class="uk-text-small">(Ctl-Shift-T)</span> //-->
                            </form-checkbox>
                        </div>

                        <div
                            v-for="request_type in conditional_request_types"
                            :key="request_type.id"
                            class="uk-width-1-1"
                        >
                            <div
                                class="uk-grid uk-grid-small"
                                data-uk-grid-margin
                            >
                                <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-small-top">
                                    {{ request_type.name }}
                                </div>
                                <div class="uk-width-2-5">
                                    <form-checkbox
                                        id="requests_for_materials"
                                        :value="!!edited_record.additional_info.requests_for_materials && (edited_record.additional_info.requests_for_materials.indexOf(request_type.id) >= 0)"
                                        :name="request_type.name"
                                        class="uk-margin-small-top"
                                        :shortkey="['ctrl', 'shift', request_for_material_toggle_shortcut(request_type.name).toLowerCase()]"
                                        @input="toggle_requests_for_material(request_type.id)"
                                    >
                                        <!-- <span class="uk-text-small">(Ctl-Shift-{{ request_for_material_toggle_shortcut(request_type.name) }})</span> //-->
                                    </form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="uk-width-large-3-10 uk-width-small-1-1 uk-margin-bottom">
                    <h3>Additional Data</h3>
                    <div class="uk-grid uk-grid-small uk-flex-top" data-uk-grid-margin>
                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-small-top">
                            Primary Deliverable
                        </div>
                        <div class="uk-width-2-5">
                            <form-select
                                id="primary_deliverable"
                                name="primary_deliverable"
                                v-model="edited_record.additional_info.primary_deliverable"
                                :options="primary_deliverables"
                                :required="false"
                                width="1-1"
                                @input="save_additional_info"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-top">
                            Pro-bono
                        </div>
                        <div class="uk-width-2-5">
                            <form-checkbox
                                id="pro_bono"
                                v-model="edited_record.additional_info.pro_bono"
                                name="pro_bono"
                                class="uk-margin-small-top"
                                @input="save_additional_info"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-top">
                            On multiple teams?
                        </div>
                        <div class="uk-width-2-5">
                            <form-checkbox
                                id="has_other_teams"
                                v-model="edited_record.additional_info.has_other_teams"
                                name="has_other_teams"
                                class="uk-margin-small-top"
                                @input="save_additional_info"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-top">
                            Did we pursue?
                        </div>
                        <div class="uk-width-2-5">
                            <form-checkbox
                                id="pursue"
                                v-model="edited_record.additional_info.pursue"
                                name="pursue"
                                class="uk-margin-small-top"
                                @input="save_additional_info"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-text-right uk-width-3-5 uk-margin-top">
                            Extenuating Circumstances
                        </div>
                        <div class="uk-width-2-5">
                            <form-checkbox
                                id="extenuating_circumstances"
                                v-model="edited_record.additional_info.extenuating_circumstances"
                                name="extenuating_circumstances"
                                class="uk-margin-small-top"
                                @input="save_additional_info"
                            />
                        </div>

                        <div class="uk-form-controls-text uk-text-bold uk-width-1-1 uk-margin-large-top">
                            Other Information
                        </div>
                        <div class="uk-width-1-1">
                            <form-text-area
                                id="other_info"
                                name="other_info"
                                v-model="edited_record.additional_info.other_info"
                                width="1-1"
                                :required="false"
                                @input="save_additional_info"
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
        <div class="uk-text-center">
            <base-button flavor="link" size="small" color="danger" class="uk-margin-top" @click.prevent="confirm_record_delete">Delete</base-button>
        </div>
    </page>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"
import orderBy from "lodash/orderBy"

import is_page from "../../mixins/IsPage"
import is_record_editor from "../../mixins/IsRecordEditor"
import handles_project_requests from "./HandlesProjectRequests"

import FormInput from "../../ui/FormInput"
import FormSelectUser from "../../ui/FormSelectUser"
import AutoSaveAddressForm from "../../ui/AutoSaveAddressForm"
import ProjectPicker from "../../ui/ProjectPicker"
import FirmPicker from "../../ui/FirmPicker"
import FormRadio from "../../ui/FormRadio"
import FormCheckbox from "../../ui/FormCheckbox"
import FormSizeControl from "../../ui/FormSizeControl"
import CategoryPicker from "../../ui/CategoryPicker"
import FormDateInput from "../../ui/FormDateInput"
import FormSelect from "../../ui/FormSelect"
import FormTextArea from "../../ui/FormTextArea"
import BaseButton from "../../ui/BaseButton"
import DropdownButton from "../../ui/DropdownButton"
import OpenIcon from "../../ui/OpenIcon"

export default {
    mixins: [is_page, is_record_editor, handles_project_requests],
    components: {
        FormSelectUser,
        FormInput,
        AutoSaveAddressForm,
        ProjectPicker,
        FirmPicker,
        FormRadio,
        FormCheckbox,
        FormSelect,
        FormTextArea,
        FormSizeControl,
        CategoryPicker,
        FormDateInput,
        BaseButton,
        DropdownButton,
        OpenIcon
    },
    mounted () {
        this.$store.dispatch("FETCH_RECORDS", { entity: "user" })
        this.set_first_focus()
    },
    methods: {
        statused (status) {
            if(!this.can("project_request.view")) status = 401
            this.page_status = status
            this.waiting = false
        },
        set_first_focus () {
            setTimeout(() => {
                const node = document.querySelector("#champion_id")
                if(node) node.focus({ focusVisible: true })
                else this.set_first_focus()
            }, 400)
        },
        available_other_recipients (user_id) {
            return orderBy(this.entity_records("user").filter(user => {
                return !user.deleted_at && ((user.id === user_id) || (this.edited_record.other_recipient_ids.indexOf(user.id) < 0))
            }), "initials", "asc")
        },
        change_other_recipients (value, old_user_id = 0) {
            const other_recipient_ids = [...(this.edited_record.other_recipient_ids || [])]
            const index = other_recipient_ids.indexOf(old_user_id || value)
            if(index >= 0) other_recipient_ids[index] = `${other_recipient_ids[index]}->${value}`
            else if(value) other_recipient_ids.push(value)
            value = other_recipient_ids

            this.save_field(value, "other_recipient_ids")
        },
        remove_other_recipient (user_id) {
            const other_recipient = this.user_by_id(user_id)
            if(!other_recipient) return
            this.$confirm({
                type: "error",
                title: `Remove ${other_recipient.initials}`,
                message: "Are you sure you want to remove '" + other_recipient.initials + "' from this request?",
                cancel: "Keep",
                ok: "Remove",
                then: (confirmed) => {
                    if(confirmed) this.change_other_recipients(0, user_id)
                }
            })
        },
        save_additional_info (value, field) {
            const additional_info = { ...this.edited_record.additional_info }
            additional_info[field] = value
            this.save_field(additional_info, "additional_info")
        },
        save_schedule (value, field) {
            const schedule = { ...this.edited_record.additional_info.schedule }
            schedule[field] = value
            this.save_additional_info(schedule, "schedule")
        },
        toggle_category (checked, category_id) {
            const category_ids = [...this.edited_record.category_ids]
            const index = category_ids.indexOf(category_id)
            if(checked && (index < 0)) category_ids.push(category_id)
            else if(!checked && (index >= 0)) category_ids.splice(index, 1)
            this.save_field(category_ids, "category_ids")
        },
        request_for_material_toggle_shortcut (request_type_name) {
            return request_type_name[0]
        },
        toggle_requests_for_material (request_type) {
            if(request_type === "*") this.save_additional_info((!!this.edited_record.additional_info.requests_for_materials && !this.edited_record.additional_info.requests_for_materials.length) ? null : [], "requests_for_materials")
            else {
                const requests_for_materials = this.edited_record.additional_info.requests_for_materials ? [...this.edited_record.additional_info.requests_for_materials] : []
                const index = requests_for_materials.indexOf(request_type)
                if(index < 0) requests_for_materials.push(request_type)
                else if(index >= 0) requests_for_materials.splice(index, 1)
                this.save_additional_info(requests_for_materials.length ? requests_for_materials : null, "requests_for_materials")
            }
        },
        confirm_verdict (accepted) {
            this.$confirm({
                type: accepted ? "warning" : "error",
                title: accepted ? "Accept RFP" : "Deny RFP",
                message: (accepted ? "Are you sure you want to acept this RFP? It will create a new prospective project from it." : "Are you sure you want to deny this RFP? It will no longer be available.") + " This operation can not be undone.",
                cancel: "Cancel",
                ok: accepted ? "Accept" : "Deny",
                then: (confirmed) => {
                    if(confirmed) {
                        this.save_data_for_record_id(
                            this.entity,
                            this.edited_record.id,
                            { verdict: accepted ? this.$root.constants.project_request_verdicts.VERDICT_ACCEPT : this.$root.constants.project_request_verdicts.VERDICT_REJECT }
                        ).then(record => {
                            if(accepted) this.$router.replace({ name: "prospective_project.edit", params: { id: record.project_id } })
                            else this.$router.replace({ name: "project_request.list" })
                        }).catch(error => {
                            this.$error(error.message)
                        })
                    }
                }
            })
        },
        copy () {
            return this.copy_record(this.edited_record)
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        page_title () {
            if(!this.edited_record || !this.edited_record.name) return (this.edited_record && this.edited_record.id) ? "Project Request" : "New Project Request"
            return this.edited_record.name
        },
        sorted_other_recipient_ids () {
            if(!this.edited_record) return []
            return orderBy(this.edited_record.other_recipient_ids, (recipient_id) => {
                return this.user_by_id(recipient_id).initials
            }, "asc")
        },
        current_physical_address () {
            return this.current_physical_address_for_record(this.edited_record)
        },
        is_draft () {
            return this.record_is_draft(this.edited_record)
        },
        conditional_request_types () {
            if(!!this.edited_record.additional_info.requests_for_materials && !this.edited_record.additional_info.requests_for_materials.length) return []
            return this.request_types
        },
        primary_deliverables () {
            return orderBy(this.dictionary("primary_deliverable"), "name", "asc")
        }
    },
    data () {
        return {
            entity: "project_request",
            relation_ids: ["address", "firm", "project"]
        }
    }
}
</script>
